import { Badge } from '@/components/shadcn/badge'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { ColumnDef } from '@tanstack/react-table'
import { RowActionsMenu } from './RowActionsMenu'

type ExistingAiGateway = RestSchemas['Messages.AiGateway.ExistingAiGateway']

function formatLinks(gateway: ExistingAiGateway): string {
  const links = gateway.links ?? []
  if (links.length === 0) return 'None'
  return links
    .map((link) => {
      if (link.workspaceId != null) return `Workspace: ${link.workspaceLabel ?? link.workspaceId}`
      if (link.applicationId != null) return `App: ${link.applicationName ?? link.applicationId}`
      if (link.projectId != null) return `Project: ${link.projectName ?? link.projectId}`
      return 'Link'
    })
    .join('; ')
}

export const columns: ColumnDef<ExistingAiGateway>[] = [
  {
    id: 'name',
    accessorFn: (row) => row.name,
    header: ({ column }) => <SortingHeaderButton column={column}>Name</SortingHeaderButton>,
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    id: 'provider',
    accessorFn: (row) => row.providerName ?? row.providerKey ?? '',
    header: ({ column }) => <SortingHeaderButton column={column}>Provider</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span>{row.original.providerName ?? '—'}</span>
        <span className="text-xs text-muted-foreground">{row.original.providerKey}</span>
      </div>
    ),
  },
  {
    id: 'models',
    header: 'Allowed models',
    cell: ({ row }) => {
      const models = row.original.allowedModels ?? []
      if (models.length === 0) return <span className="text-muted-foreground">All / none set</span>
      return (
        <div className="flex flex-wrap gap-1">
          {models.slice(0, 4).map((model) => (
            <Badge variant="secondary" key={model}>
              {model}
            </Badge>
          ))}
          {models.length > 4 && (
            <Badge variant="outline">+{models.length - 4}</Badge>
          )}
        </div>
      )
    },
  },
  {
    id: 'owner',
    accessorFn: (row) => row.ownerEmail ?? String(row.ownerId),
    header: ({ column }) => <SortingHeaderButton column={column}>Owner</SortingHeaderButton>,
    cell: ({ row }) => (
      <span className="text-sm">{row.original.ownerEmail ?? `User ${row.original.ownerId}`}</span>
    ),
  },
  {
    id: 'links',
    header: 'Links',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{formatLinks(row.original)}</span>
    ),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex justify-end pr-5">
        <RowActionsMenu gateway={row.original} />
      </div>
    ),
  },
]
